import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, FileText, FolderOpen, Gavel, Landmark, Receipt, Search, Users, type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, CommandPalette, MOD, Shortcut, useKeyboardShortcuts, useMicrocopy, type PaletteGroup } from "@ncp/ui";

type Kind = "case" | "party" | "decision" | "payment" | "company" | "policy";
interface SearchHit { kind: Kind; section: string; title: string; titleAr?: string | null; detail?: string | null; detailAr?: string | null; reference?: string | null; href: string }

const ICONS: Record<Kind, LucideIcon> = { case: FolderOpen, party: Users, decision: Gavel, payment: Receipt, company: Building2, policy: Landmark };
const ORDER: Kind[] = ["case", "decision", "party", "company", "policy", "payment"];

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

/** The header search on every screen (`⌘K` / `Ctrl+K`): files in the three sections and other requests, companies and reference numbers. */
export function GlobalSearch() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const m = useMicrocopy();
  const api = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const term = useDebounced(query.trim(), 200);

  useKeyboardShortcuts({ onPalette: () => setOpen(true), onSearch: () => setOpen(true), onHelp: () => undefined, onGo: () => undefined });

  const hits = useQuery({
    queryKey: ["global-search", term],
    queryFn: () => api.get<SearchHit[]>(`/api/search?q=${encodeURIComponent(term)}`),
    enabled: open && term.length >= 2,
    staleTime: 30_000,
  });

  const ar = locale === "ar";
  const groups: PaletteGroup[] = ORDER.map((kind) => ({
    id: kind,
    heading: t(`backoffice:search.kinds.${kind}`),
    items: (hits.data ?? []).filter((h) => h.kind === kind).map((h, i) => {
      const title = ar && h.titleAr ? h.titleAr : h.title;
      const detail = ar && h.detailAr ? h.detailAr : h.detail;
      return {
        id: `${kind}-${i}-${h.href}`,
        icon: ICONS[kind] ?? FileText,
        label: <span className="flex min-w-0 flex-col"><span className="truncate">{title}</span>{detail ? <span className="truncate text-xs text-muted-foreground">{detail}</span> : null}</span>,
        hint: <span className="flex items-center gap-2">{h.reference ? <bdi className="tabular">{h.reference}</bdi> : null}<span>{t(`backoffice:search.sections.${h.section}`)}</span></span>,
        onSelect: () => navigate(h.href),
      };
    }),
  }));

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="hidden w-60 justify-start gap-2 text-muted-foreground md:inline-flex xl:w-80" aria-label={t("backoffice:search.open")} aria-keyshortcuts={MOD === "⌘" ? "Meta+K" : "Control+K"}>
        <Search className="size-4" />
        <span className="flex-1 text-start">{t("actions.search")}</span>
        <Shortcut keys={[MOD, "K"]} />
      </Button>
      <Button variant="ghost" size="icon-sm" onClick={() => setOpen(true)} className="md:hidden" aria-label={t("backoffice:search.open")}>
        <Search />
      </Button>
      <CommandPalette
        open={open}
        onOpenChange={setOpen}
        groups={groups}
        query={query}
        onQueryChange={setQuery}
        label={t("backoffice:search.open")}
        placeholder={m("searchPlaceholder")}
        emptyText={term.length < 2 ? m("searchHint") : hits.isFetching ? "…" : m("searchEmpty")}
        shouldFilter={false}
      />
    </>
  );
}
